import React, { useEffect, useRef, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import {
  useFetchEcommerceColorsMutation,
  useAddEcommerceColorMutation,
} from "../../../features/tenant/ecommerceProduct/ecommerceColorsApi";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import toast from "react-hot-toast";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import { HiOutlinePlus } from "react-icons/hi";

const EcommerceColorMaster = () => {
  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme.color_shades;
  const vendorData = JSON.parse(sessionStorage.getItem("vendorData"));
  const tenant_id = vendorData.tenant_id;
  const token = sessionStorage.getItem("access_token");

  const [colors, setColors] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  // Form states
  const [hexCode, setHexCode] = useState("#4F46E5");
  const [colorName, setColorName] = useState("");
  const [hexInput, setHexInput] = useState("#4F46E5");

  const colorInputRef = useRef(null);

  // API hooks
  const [fetchColors, { isLoading: colorsLoading }] = useFetchEcommerceColorsMutation();
  const [addColor, { isLoading: addLoading }] = useAddEcommerceColorMutation();

  const loadColors = () => {
    fetchColors({ slug: tenant_id, token })
      .unwrap()
      .then((res) => {
        if (res?.body) {
          setColors(res.body);
        }
      })
      .catch(() => {
        toast.error("Failed to load colors");
      });
  };

  useEffect(() => {
    loadColors();
  }, []);

  const handleColorPickerChange = (e) => {
    const val = e.target.value;
    setHexCode(val);
    setHexInput(val);
  };

  const handleHexInputChange = (e) => {
    const val = e.target.value;
    setHexInput(val);
    // Only update color if valid hex
    if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
      setHexCode(val);
    }
  };

  const resetForm = () => {
    setColorName("");
    setHexCode("#4F46E5");
    setHexInput("#4F46E5");
  };

  const handleAddColor = (e) => {
    e.preventDefault();
    if (!colorName.trim()) {
      toast.error("Please enter a color name");
      return;
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(hexCode)) {
      toast.error("Please enter a valid hex color code");
      return;
    }

    const payload = {
      slug: tenant_id,
      token,
      color_name: colorName.trim(),
      hex_code: hexCode,
    };

    addColor(payload)
      .unwrap()
      .then(() => {
        toast.success("Color added successfully");
        setShowAddModal(false);
        resetForm();
        loadColors();
      })
      .catch((err) => {
        toast.error(err.data?.message || "Failed to add color");
      });
  };

  return (
    <div className="flex w-full bg-gray-200 h-full justify-center min-h-screen">
      <div className="w-full">
        <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold flex justify-between items-center pr-6">
          <div>
            Color Master
            <LinksComponent />
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 text-white px-4 py-2 rounded text-sm hover:opacity-90 shadow-md font-medium"
            style={{ backgroundColor: primaryColor["700"] }}
          >
            <HiOutlinePlus /> Add Color
          </button>
        </div>

        <div className="p-4">
          <div className="p-4 w-full bg-white shadow rounded-lg min-h-[400px]">
            {colorsLoading && <CircleLoaderComponent />}

            {!colorsLoading && colors.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-gray-500">
                <p className="text-lg">No colors created yet</p>
                <p className="text-sm">Click "Add Color" to create a color.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Preview
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Color Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Hex Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {colors.map((item) => (
                      <tr key={item.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-8 h-8 rounded-full border-2 border-white shadow-md ring-1 ring-gray-200"
                              style={{ backgroundColor: item.hex_code }}
                              title={item.hex_code}
                            />
                            <div
                              className="w-16 h-4 rounded"
                              style={{ backgroundColor: item.hex_code }}
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800">
                          {item.color_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded border border-gray-200"
                              style={{ backgroundColor: item.hex_code }}
                            />
                            <span className="text-sm font-mono text-gray-600 uppercase">
                              {item.hex_code}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              item.status === "1"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {item.status === "1" ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <ModalComponent size="md" show={showAddModal} setShow={setShowAddModal}>
          <form onSubmit={handleAddColor} className="p-4 w-full">
            <h3 className="text-lg font-bold mb-4 text-gray-800">Add Color</h3>
            <div className="flex flex-col gap-4">

              <InputComponent
                name="Color Name"
                type="text"
                value={colorName}
                placeholder="e.g. Sunset Orange"
                setValue={setColorName}
                required
              />

              {/* Color Picker Section */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">Pick Color</label>

                {/* Live preview bar */}
                <div
                  className="w-full h-10 rounded-lg border border-gray-200 shadow-inner transition-colors duration-200"
                  style={{ backgroundColor: hexCode }}
                />

                {/* Picker button + hex input row */}
                <div className="flex items-center gap-3">
                  {/* Styled color swatch button */}
                  <button
                    type="button"
                    onClick={() => colorInputRef.current?.click()}
                    className="relative flex-shrink-0 w-12 h-12 rounded-lg border-2 border-gray-300 shadow-md cursor-pointer hover:scale-105 transition-transform overflow-hidden"
                    style={{ backgroundColor: hexCode }}
                    title="Click to open color picker"
                  >
                    {/* Invisible native color input */}
                    <input
                      ref={colorInputRef}
                      type="color"
                      value={hexCode}
                      onChange={handleColorPickerChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      tabIndex={-1}
                    />
                  </button>

                  {/* Hex text input */}
                  <div className="flex-1 flex items-center border border-gray-300 rounded-lg overflow-hidden shadow-sm bg-white">
                    <span className="px-3 py-3 text-gray-400 text-sm font-mono bg-gray-50 border-r border-gray-200 select-none">
                      #
                    </span>
                    <input
                      type="text"
                      value={hexInput.replace("#", "")}
                      onChange={(e) =>
                        handleHexInputChange({ target: { value: "#" + e.target.value } })
                      }
                      maxLength={6}
                      placeholder="4F46E5"
                      className="flex-1 px-3 py-3 text-sm font-mono text-gray-700 outline-none uppercase bg-white"
                    />
                    {/* Color dot preview in text box */}
                    <div
                      className="w-6 h-6 rounded-full border border-gray-200 mx-2 flex-shrink-0"
                      style={{ backgroundColor: hexCode }}
                    />
                  </div>
                </div>

                <p className="text-xs text-gray-400">
                  Click the swatch to open the color picker, or type a hex code directly.
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addLoading}
                  className="px-4 py-2 text-white rounded text-sm hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: primaryColor["700"] }}
                >
                  {addLoading ? "Adding..." : "Add Color"}
                </button>
              </div>
            </div>
          </form>
        </ModalComponent>
      )}
    </div>
  );
};

export default EcommerceColorMaster;
